import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useKickoffAndPoll} from '../../../../../hooks/useKickoffAndPoll';
import useSlackService from '../../../../../services/slack';
import {useIntegrationStatus} from '../../../../../hooks/useIntegrationStatus';
import { setSlackUserSyncRunning } from '../../../../../redux/slices/settings';
import { useJwt } from '../../../../../helpers/common';

const SlackUsersSyncBanner: React.FC = () => {
  const JWTdata: any = useJwt();
  const dispatch = useDispatch();
  const org_id = JWTdata?.org_id;
  const { isIntegrationActive } = useIntegrationStatus();
  const isSlackConnected = isIntegrationActive('slack');
  const slackUserSyncRunning: boolean = useSelector((state: any) => state.settings.slackUserSyncRunning);
  const { syncSlackUsers, syncSlackUsersStatus, getSlackTargets } = useSlackService();

  const onComplete = async () => {
    dispatch(setSlackUserSyncRunning(false));
    if (org_id) {
      try { 
        await getSlackTargets(org_id); 
      } 
      catch (e) {}
    }
  }

  const { start: startSlackUsersSync } = useKickoffAndPoll({
    kickoff: async () => {
      await syncSlackUsers();
      const running = await syncSlackUsersStatus();
      return { processing_status_stages: !!running };
    },
    check: async () => {
      const running = await syncSlackUsersStatus();
      return { processing_status_stages: !!running };
    },
    onComplete: onComplete,
    options: {
      intervalMs: 1000,
      maxWaitMs: 5 * 60 * 1000,
      persistAcrossUnmount: true,
      onStatusChange: (running) => dispatch(setSlackUserSyncRunning(running))
    }
  });

  // Resume polling on refresh if backend says it's already running (avoid re-kickoff)
  const { start: resumeSlackUsersPolling } = useKickoffAndPoll({
    kickoff: async () => {
      // No-op kickoff; immediately indicate "keep polling"
      return { processing_status_stages: true };
    },
    check: async () => {
      const running = await syncSlackUsersStatus();
      return { processing_status_stages: !!running };
    },
    onComplete: onComplete,
    options: {
      intervalMs: 1000,
      maxWaitMs: 5 * 60 * 1000,
      persistAcrossUnmount: true,
      onStatusChange: (running) => dispatch(setSlackUserSyncRunning(running))
    }
  });

  const callSyncSlackUsersStatus = async () => {
    try {
      const running = await syncSlackUsersStatus();
      dispatch(setSlackUserSyncRunning(!!running));
      // Only one banner should start resume polling; guard on existing running flag
      if (running && !slackUserSyncRunning) {
        // Start polling without re-triggering the sync job
        await resumeSlackUsersPolling();
      }
    } catch (e) {}
  }

  // Initialize Slack users sync status so we reflect background syncs
  useEffect(() => {
    callSyncSlackUsersStatus()
  }, []);

  if (!isSlackConnected) return null;

  return (
    <div className="mb-3 px-3 py-2 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-blue-800 dark:text-blue-300">
          Keep Slack users up to date across the app.
        </div>
        <button
          type="button"
          className={`inline-flex items-center text-xs px-3 py-1.5 rounded bg-primary-light hover:bg-primary-hover text-white disabled:opacity-60 disabled:cursor-not-allowed`}
          onClick={async () => {
            if (slackUserSyncRunning) return;
            await startSlackUsersSync();
          }}
          disabled={slackUserSyncRunning}
        >
          {slackUserSyncRunning && (
            <span className="w-3 h-3 mr-2 border-2 border-white/90 border-t-transparent rounded-full animate-spin"></span>
          )}
          {slackUserSyncRunning ? 'Syncing Slack users…' : 'Sync users'}
        </button>
      </div>
    </div>
  );
};

export default SlackUsersSyncBanner;